import { Configuration, LogLevel, PublicClientApplication } from "@azure/msal-browser";
import { appConfig } from "../../lib/config/appConfig";

// ── MSAL configuration ──────────────────────────────────────────────────────
// Uses Auth Code + PKCE flow (the modern SPA pattern) by default in MSAL v3.
export const msalConfig: Configuration = {
  auth: {
    clientId: appConfig.auth.clientId,
    authority: appConfig.auth.authority,
    redirectUri: appConfig.auth.redirectUri,
    postLogoutRedirectUri: appConfig.auth.redirectUri,
    navigateToLoginRequestUrl: true
  },
  cache: {
    cacheLocation: "localStorage",
    // true lets the silent-refresh iframe read auth state in browsers that
    // partition storage (Edge) — fixes monitor_window_timeout.
    storeAuthStateInCookie: true
  },
  system: {
    // give the silent iframe / popup more time to complete the hash handshake
    iframeHashTimeout: 10000,
    windowHashTimeout: 10000,
    loadFrameTimeout: 10000,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log(`[MSAL][${LogLevel[level]}] ${message}`);
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Warning
    }
  }
};

// ── Token scopes ────────────────────────────────────────────────────────────
// Separate scopes for SharePoint REST vs Microsoft Graph — MSAL will acquire
// different tokens for each resource. Do NOT mix them in a single request.

/** Scopes for SharePoint REST API calls (CRM_Customers list, etc.) */
export const sharepointScopes = {
  scopes: [`${appConfig.sharepoint.resource}/AllSites.Manage`]
};

/** Scopes for Microsoft Graph calls (user profile, etc.) */
export const graphScopes = {
  scopes: ["User.Read", "Sites.Read.All"]
};

/**
 * Scope for sending mail as the signed-in user (HRIS leave-approval emails).
 * Requires the app registration to have the delegated Microsoft Graph
 * `Mail.Send` permission; kept isolated so it's only requested when the HR
 * user opts into approval emails, and its consent never blocks app load.
 */
export const mailScopes = {
  scopes: ["Mail.Send"]
};

/**
 * Scope for calling our OWN Functions API (`/api/*`), e.g. the E.V.A and bill
 * extraction endpoints. Requires the app registration to "Expose an API" with an
 * `access_as_user` scope under Application ID URI `api://<clientId>`.
 */
export const apiScopes = {
  scopes: [`api://${appConfig.auth.clientId}/access_as_user`]
};

/** Initial login request — just asks for basic profile to establish session. */
export const loginRequest = {
  scopes: ["User.Read", "openid", "profile"]
};

// ── Singleton MSAL instance ─────────────────────────────────────────────────
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * True when THIS window is the sign-in popup MSAL opened (acquireTokenPopup).
 *
 * MSAL names its popup "msal.<clientId>.<id>" and the identity provider sends
 * it back to our own origin with the answer in the URL hash. The OPENER reads
 * that hash and closes the popup — so the popup must do NOTHING with it. Before
 * this guard the popup booted the whole app: the router and the auth provider
 * ran, the hash was consumed or navigated away, the opener never saw it and
 * gave up with monitor_window_timeout, and the popup sat there showing the
 * Home screen. "Grant access" then looped for ever, for any account whose
 * silent sign-in had to fall back to the popup.
 */
export function isMsalPopupWindow(w?: Pick<Window, "opener" | "name">): boolean {
  const win = w ?? (typeof window === "undefined" ? undefined : window);
  if (!win) return false; // no window at all (a test under node): not a popup
  return !!win.opener && win.opener !== win && typeof win.name === "string" && win.name.startsWith("msal.");
}

// Handle redirect callback (first load after login).
// Must be called before any other MSAL method — and never in a sign-in popup,
// whose hash belongs to the window that opened it.
msalInstance.initialize().then(() => {
  if (isMsalPopupWindow()) return;
  msalInstance.handleRedirectPromise().then((result) => {
    // The account that just signed in is the one the API token is read for.
    if (result?.account) msalInstance.setActiveAccount(result.account);
  }).catch((error) => {
    // eslint-disable-next-line no-console
    console.error("MSAL redirect handling failed:", error);
  });
});
